import { createSlice } from '@reduxjs/toolkit';
import i18n from '../../../i18n';

const initialState = {
  alertConfigurations: [],
  units: [],
  alertTypes: [],
  notificationMethods: [],
  loading: false,
  error: null,
  successMessage: null,
  selectedAlert: null,
  // Pagination
  page: 1,
  pageSize: 10,
  total: 0,
  // Sorting
  sortColumn: 'id',
  sortDirection: 'desc',
  // Filtering
  filterAlertType: '',
  filterIsActive: null,
  filterUnitId: null,
};

const alertConfigurationsSlice = createSlice({
  name: 'alertConfigurations',
  initialState,
  reducers: {
    // Fetch init data
    fetchInit: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchInitSuccess: (state, action) => {
      state.loading = false;
      state.units = action.payload.units || [];
      state.alertTypes = action.payload.alertTypes || [];
      state.notificationMethods = action.payload.notificationMethods || [];
      state.error = null;
    },
    fetchInitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch alert configurations
    fetchAlertConfigurations: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchAlertConfigurationsSuccess: (state, action) => {
      state.loading = false;
      state.alertConfigurations = action.payload.data || [];
      state.total = action.payload.meta?.total || 0;
      state.page = action.payload.meta?.page || 1;
      state.pageSize = action.payload.meta?.size || 10;
      state.error = null;
    },
    fetchAlertConfigurationsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch alert configuration by ID
    fetchAlertConfigurationById: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchAlertConfigurationByIdSuccess: (state, action) => {
      state.loading = false;
      state.selectedAlert = action.payload;
      state.error = null;
    },
    fetchAlertConfigurationByIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Create alert configuration
    createAlertConfiguration: (state) => {
      state.loading = true;
      state.error = null;
    },
    createAlertConfigurationSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = i18n.t('alertConfigurations.success.created');
      state.error = null;
    },
    createAlertConfigurationFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Update alert configuration
    updateAlertConfiguration: (state) => {
      state.loading = true;
      state.error = null;
    },
    updateAlertConfigurationSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = i18n.t('alertConfigurations.success.updated');
      state.error = null;
    },
    updateAlertConfigurationFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Delete alert configuration
    deleteAlertConfiguration: (state) => {
      state.loading = true;
      state.error = null;
    },
    deleteAlertConfigurationSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = i18n.t('alertConfigurations.success.deleted');
      state.error = null;
    },
    deleteAlertConfigurationFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Toggle active
    toggleActive: (state) => {
      state.loading = true;
      state.error = null;
    },
    toggleActiveSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = i18n.t('alertConfigurations.success.toggled');
      state.error = null;
    },
    toggleActiveFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1;
    },

    // Sorting
    setSortColumn: (state, action) => {
      state.sortColumn = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },

    // Filtering
    setFilterAlertType: (state, action) => {
      state.filterAlertType = action.payload;
      state.page = 1;
    },
    setFilterIsActive: (state, action) => {
      state.filterIsActive = action.payload;
      state.page = 1;
    },
    setFilterUnitId: (state, action) => {
      state.filterUnitId = action.payload;
      state.page = 1;
    },

    // Clear messages
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearSelectedAlert: (state) => {
      state.selectedAlert = null;
    },
  },
});

export const {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchAlertConfigurations,
  fetchAlertConfigurationsSuccess,
  fetchAlertConfigurationsFailure,
  fetchAlertConfigurationById,
  fetchAlertConfigurationByIdSuccess,
  fetchAlertConfigurationByIdFailure,
  createAlertConfiguration,
  createAlertConfigurationSuccess,
  createAlertConfigurationFailure,
  updateAlertConfiguration,
  updateAlertConfigurationSuccess,
  updateAlertConfigurationFailure,
  deleteAlertConfiguration,
  deleteAlertConfigurationSuccess,
  deleteAlertConfigurationFailure,
  toggleActive,
  toggleActiveSuccess,
  toggleActiveFailure,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterAlertType,
  setFilterIsActive,
  setFilterUnitId,
  clearError,
  clearSuccessMessage,
  clearSelectedAlert,
} = alertConfigurationsSlice.actions;

// Selectors
export const selectAlertConfigurations = (state) => state.alertConfigurations?.alertConfigurations || [];
export const selectAlertConfigurationsLoading = (state) => state.alertConfigurations?.loading || false;
export const selectAlertConfigurationsError = (state) => state.alertConfigurations?.error;
export const selectAlertConfigurationsSuccessMessage = (state) => state.alertConfigurations?.successMessage;
export const selectSelectedAlert = (state) => state.alertConfigurations?.selectedAlert;
export const selectUnits = (state) => state.alertConfigurations?.units || [];
export const selectAlertTypes = (state) => state.alertConfigurations?.alertTypes || [];
export const selectNotificationMethods = (state) => state.alertConfigurations?.notificationMethods || [];
export const selectAlertConfigurationsPage = (state) => state.alertConfigurations?.page || 1;
export const selectAlertConfigurationsPageSize = (state) => state.alertConfigurations?.pageSize || 10;
export const selectAlertConfigurationsTotal = (state) => state.alertConfigurations?.total || 0;
export const selectAlertConfigurationsSortColumn = (state) => state.alertConfigurations?.sortColumn || 'id';
export const selectAlertConfigurationsSortDirection = (state) => state.alertConfigurations?.sortDirection || 'desc';
export const selectFilterAlertType = (state) => state.alertConfigurations?.filterAlertType || '';
export const selectFilterIsActive = (state) => state.alertConfigurations?.filterIsActive ?? null;
export const selectFilterUnitId = (state) => state.alertConfigurations?.filterUnitId;

export default alertConfigurationsSlice.reducer;

